import React, { useRef, useState, useEffect } from 'react';
import QRCode from 'qrcode';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { 
  Send, Download, CheckCircle, Copy, Printer,
  FileText, Eye, AlertTriangle, Clock, FileOutput, FileDown, Ban, Plus
} from 'lucide-react';

// Light mode compatible status colors with better contrast
const statusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-600 dark:bg-slate-500', textColor: 'text-white', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-600 dark:bg-blue-500', textColor: 'text-white', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-600 dark:bg-purple-500', textColor: 'text-white', icon: Eye },
  paid: { label: 'Paid', color: 'bg-green-600 dark:bg-green-500', textColor: 'text-white', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-600 dark:bg-red-500', textColor: 'text-white', icon: AlertTriangle },
  void: { label: 'Void', color: 'bg-gray-600 dark:bg-gray-500', textColor: 'text-white', icon: Ban },
};

const paymentTermsLabels = {
  due_on_receipt: 'Due on Receipt',
  net_15: 'Net 15',
  net_30: 'Net 30'
};

const PAYMENT_METHODS = [
  { value: 'Check', label: 'Check' },
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Venmo', label: 'Venmo' },
  { value: 'CashApp', label: 'CashApp' },
  { value: 'Zelle', label: 'Zelle' },
  { value: 'Wire Transfer', label: 'Wire Transfer' },
  { value: 'Other', label: 'Other' },
];

const VOID_REASONS = [
  { value: 'case_settled', label: 'Case Settled' },
  { value: 'case_dismissed', label: 'Case Dismissed' },
  { value: 'duplicate', label: 'Duplicate Invoice' },
  { value: 'client_request', label: 'Client Request' },
  { value: 'billing_error', label: 'Billing Error' },
  { value: 'service_not_completed', label: 'Service Not Completed' },
  { value: 'other', label: 'Other' },
];

export default function InvoiceViewDialog({ open, onOpenChange, invoice, profile, invoiceSettings, onAction, autoOpenPayment }) {
  const printRef = useRef(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState('');  // Manual paid date
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidNotes, setVoidNotes] = useState('');
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  // Edit an already-recorded payment (method / date / notes)
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState('');
  const [editPaymentMethod, setEditPaymentMethod] = useState('Check');
  const [editPaymentNotes, setEditPaymentNotes] = useState('');
  const [editPaymentDate, setEditPaymentDate] = useState('');

  // "Scan to pay" QR code pointing to the public invoice/pay page.
  // Only rendered for unpaid, sent invoices (not draft/paid/void).
  const [qrDataUrl, setQrDataUrl] = useState('');
  useEffect(() => {
    const balance = invoice?.balance_due != null ? invoice.balance_due : (invoice?.total || 0);
    const payable = !!(invoice?.id && invoice?.status && !['paid', 'void', 'draft'].includes(invoice.status) && balance > 0.05);
    if (!payable) { setQrDataUrl(''); return; }
    const payUrl = `${window.location.origin}/invoice/${invoice.id}?src=qr`;
    QRCode.toDataURL(payUrl, { width: 240, margin: 1, color: { dark: '#0f172a', light: '#ffffff' } })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [invoice?.id, invoice?.status, invoice?.balance_due, invoice?.total]);

  // When opened via the list's "Mark Paid" quick action, auto-open the Record Payment dialog
  useEffect(() => {
    if (open && autoOpenPayment && invoice && ['sent', 'viewed', 'overdue'].includes(invoice.status)) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      setPaymentDate(`${yyyy}-${mm}-${dd}`);
      setShowPaymentDialog(true);
    }
  }, [open, autoOpenPayment, invoice?.id, invoice?.status]);

  if (!invoice) return null;

  const StatusIcon = statusConfig[invoice.status]?.icon || FileText;

  // Determine branding to display - use custom settings if available, fallback to profile/defaults
  const brandingName = invoiceSettings?.company_name || profile?.company_name || profile?.display_name || 'Process Server';
  const brandingTagline = invoiceSettings?.tagline || '';
  const brandingLogo = invoiceSettings?.logo_url || null;
  const logoSize = invoiceSettings?.logo_size || 'medium';
  const defaultTerms = invoiceSettings?.default_terms || '';
  const footerPhone = invoiceSettings?.footer_phone || profile?.phone || '';
  const footerEmail = invoiceSettings?.footer_email || profile?.email || '';
  const footerWebsite = invoiceSettings?.footer_website || profile?.website || '';
  const footerText = invoiceSettings?.footer_text || '';

  // Theme settings
  const theme = invoiceSettings?.theme || {};
  const primaryColor = theme.primary_color || '#1e40af';
  const accentColor = theme.accent_color || '#3b82f6';
  const headerBgColor = theme.header_bg_color || '#f8fafc';
  const textColor = theme.text_color || '#1e293b';
  const fontFamily = theme.font_family || 'system';
  
  // Font family mapping
  const fontFamilyMap = {
    'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'inter': '"Inter", sans-serif',
    'roboto': '"Roboto", sans-serif',
    'open-sans': '"Open Sans", sans-serif',
    'lato': '"Lato", sans-serif',
    'poppins': '"Poppins", sans-serif',
    'playfair': '"Playfair Display", serif',
    'merriweather': '"Merriweather", serif',
    'georgia': 'Georgia, serif',
    'times': '"Times New Roman", Times, serif',
  };
  const fontFamilyStyle = fontFamilyMap[fontFamily] || fontFamilyMap['system'];

  // Logo size classes - QuickBooks-style large logos (up to 500px)
  const logoSizeClasses = {
    small: 'h-16 w-auto max-w-[120px]',
    medium: 'h-24 w-auto max-w-[200px]',
    large: 'h-32 w-auto max-w-[300px]',
    xlarge: 'h-48 w-auto max-w-[500px]'
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Format line item type for display (when description is blank)
  const formatItemType = (itemType) => {
    const typeLabels = {
      'service': 'Process Service',
      'rush_fee': 'Rush Service Fee',
      'mileage': 'Mileage',
      'filing_fee': 'Filing Fee',
      'skip_trace': 'Skip Trace Fee',
      'copies': 'Document Copies',
      'processing_fee': 'Processing Fee',
      'custom': 'Service',
      'extra_attempt': 'Additional Attempt Fee'
    };
    return typeLabels[itemType] || itemType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Service';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    // Parse the ISO string - JavaScript Date handles UTC 'Z' suffix automatically
    const date = new Date(dateStr);
    // Use consistent short format: "Feb 3, 2026, 11:06 PM" in user's local timezone
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatPaidDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    // Check if this was a manual date entry (set to noon UTC = T12:00:00Z)
    // If so, show date only without the time
    const utcHours = date.getUTCHours();
    const utcMins = date.getUTCMinutes();
    if (utcHours === 12 && utcMins === 0) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // Otherwise show full date + time in local timezone
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSend = async () => {
    try {
      await api.sendInvoice(invoice.id, { send_email: true });
      toast.success('Invoice sent to client via email');
      onAction();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleIssue = async () => {
    try {
      await api.sendInvoice(invoice.id, { send_email: false });
      toast.success('Invoice issued - deliver to client manually');
      onAction();
    } catch (error) {
      toast.error('Failed to issue invoice');
    }
  };

  const handleMarkPaid = async () => {
    // Initialize date to today in local timezone
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setPaymentDate(`${yyyy}-${mm}-${dd}`);
    // Show payment method dialog instead of immediately marking paid
    setShowPaymentDialog(true);
  };

  const confirmMarkPaid = async () => {
    try {
      // Record the payment with method, notes, and custom date
      const balanceDue = invoice.total - (invoice.total_paid || 0);
      await api.recordPayment(invoice.id, {
        amount: balanceDue,
        payment_method: paymentMethod,
        notes: paymentNotes || null,
        paid_date: paymentDate || null
      });
      toast.success('Payment recorded');
      setShowPaymentDialog(false);
      setPaymentMethod('Check');
      setPaymentNotes('');
      setPaymentDate('');
      onAction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openEditPayment = (payment) => {
    const p = payment || (invoice.payments && invoice.payments[0]);
    if (!p) return;
    setEditingPaymentId(p.id);
    setEditPaymentMethod(p.payment_method && p.payment_method !== 'Manual' ? p.payment_method : 'Check');
    setEditPaymentNotes(p.notes && p.notes !== 'Marked as paid' ? p.notes : '');
    const d = p.payment_date ? new Date(p.payment_date) : new Date();
    setEditPaymentDate(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`);
    setShowEditPaymentDialog(true);
  };

  const confirmEditPayment = async () => {
    try {
      await api.updatePayment(invoice.id, editingPaymentId, {
        payment_method: editPaymentMethod,
        notes: editPaymentNotes || null,
        paid_date: editPaymentDate || null
      });
      toast.success('Payment updated');
      setShowEditPaymentDialog(false);
      onAction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const handleVoid = () => {
    // Open the void dialog instead of using confirm()
    setVoidReason('');
    setVoidNotes('');
    setShowVoidDialog(true);
  };

  const confirmVoid = async () => {
    if (!voidReason) {
      toast.error('Please select a reason for voiding');
      return;
    }
    try {
      // Get the label for the selected reason
      const reasonLabel = VOID_REASONS.find(r => r.value === voidReason)?.label || voidReason;
      const voidInfo = voidNotes ? `${reasonLabel}: ${voidNotes}` : reasonLabel;
      
      await api.updateInvoice(invoice.id, { 
        status: 'void',
        void_reason: voidReason,
        void_notes: voidNotes,
        void_info: voidInfo,
        voided_at: new Date().toISOString()
      });
      toast.success('Invoice voided');
      setShowVoidDialog(false);
      onAction();
    } catch (error) {
      toast.error('Failed to void invoice');
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/invoice/${invoice.id}`;
    navigator.clipboard.writeText(link);
    toast.success('Invoice link copied to clipboard');
  };

  const handleSendReminder = () => {
    setReminderMessage('');
    setShowReminderDialog(true);
  };

  const confirmSendReminder = async () => {
    setIsSendingReminder(true);
    try {
      await api.sendInvoiceReminder(invoice.id, { message: reminderMessage || null });
      toast.success('Payment reminder sent to client');
      setShowReminderDialog(false);
      onAction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const generatePrintHTML = () => {
    // Determine Bill To info based on whether client has a company
    const hasCompany = invoice.client?.company;
    
    let billToName, billToCompany, billToAddress;
    
    if (hasCompany) {
      // Client is linked to a company - show company prominently
      billToName = invoice.client.company.name;
      billToCompany = invoice.client?.name ? `${invoice.client.name}${invoice.client?.title ? ` (${invoice.client.title})` : ''}` : '';
      billToAddress = [
        invoice.client?.address_street || invoice.client?.company?.address_street,
        [
          invoice.client?.address_city || invoice.client?.company?.address_city, 
          invoice.client?.address_state || invoice.client?.company?.address_state, 
          invoice.client?.address_zip || invoice.client?.company?.address_zip
        ].filter(Boolean).join(', ')
      ].filter(Boolean).join('<br>');
    } else {
      // Standalone client
      billToName = invoice.client?.name || '';
      billToCompany = invoice.client?.company_name || '';
      billToAddress = [
        invoice.client?.address_street,
        [invoice.client?.address_city, invoice.client?.address_state, invoice.client?.address_zip].filter(Boolean).join(', ')
      ].filter(Boolean).join('<br>');
    }
    
    // Server address from profile.server_address object
    const serverAddrObj = profile?.server_address;
    const serverAddr = serverAddrObj ? [
      serverAddrObj.street,
      [serverAddrObj.city, serverAddrObj.state, serverAddrObj.zip_code].filter(Boolean).join(', ')
    ].filter(Boolean).join('<br>') : '';

    const lineItemsHTML = invoice.line_items?.map(item => {
      const isProcessingFee = item.item_type === 'processing_fee';
      return `
        <tr>
          <td>${item.description || (isProcessingFee ? `Processing Fee (${item.unit_price}%)` : formatItemType(item.item_type))}</td>
          <td style="text-align: right;">${isProcessingFee ? '-' : item.quantity}</td>
          <td style="text-align: right;">${isProcessingFee ? `${item.unit_price}%` : formatCurrency(item.unit_price)}</td>
          <td style="text-align: right;">${formatCurrency(item.total)}</td>
        </tr>
      `;
    }).join('') || '';

    // Build reference fields (no service_address - server can put that in line item description)
    // Order: Entity Served, Case #, Reference #, Service Date
    const refFields = [];
    if (invoice.entity_served) refFields.push({ label: 'Entity Served', value: invoice.entity_served });
    if (invoice.case_number) refFields.push({ label: 'Case #', value: invoice.case_number });
    if (invoice.reference_number) refFields.push({ label: 'Ref #', value: invoice.reference_number });
    if (invoice.service_date) refFields.push({ label: 'Service Date', value: formatDate(invoice.service_date) });

    // Logo size mapping for print (in pixels)
    const printLogoSizes = {
      small: '60px',
      medium: '90px', 
      large: '120px',
      xlarge: '160px'
    };
    const printLogoHeight = printLogoSizes[logoSize] || printLogoSizes.medium;

    // "Scan to Pay" QR block — compact, right-aligned, only present when qrDataUrl is set.
    const qrHTML = qrDataUrl ? `
      <div class="pay-qr">
        <img src="${qrDataUrl}" alt="Scan to pay" class="pay-qr-img" />
        <div class="pay-qr-title">Scan to Pay</div>
      </div>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: ${fontFamilyStyle}; color: ${textColor}; font-size: 11px; }
          .invoice { max-width: 800px; margin: 0 auto; background: white; }
          
          .header { text-align: center; padding: 12px; margin: 8px 28px 0; background: ${headerBgColor}; border-radius: 6px; display: flex; flex-direction: column; align-items: center; }
          .logo { height: ${printLogoHeight}; max-width: 280px; object-fit: contain; display: block; margin: 0 auto; }
          .company-name { font-size: 18px; font-weight: bold; margin-bottom: 2px; color: ${primaryColor}; }
          .tagline { color: #475569; font-size: 9px; margin-top: 3px; }
          
          .meta-wrapper { padding: 6px 28px; }
          .meta-table { width: 100%; border-collapse: collapse; background: ${headerBgColor}; border: 1px solid ${accentColor}40; border-radius: 6px; }
          .meta-table td { padding: 8px 6px; text-align: center; border-left: 1px solid ${accentColor}40; vertical-align: middle; }
          .meta-table td:first-child { border-left: none; }
          .meta-label { font-size: 8px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; display: block; font-weight: 600; }
          .meta-value { font-size: 10px; font-weight: 700; margin-top: 2px; display: block; color: ${textColor}; }
          
          .content { padding: 12px 28px; }
          .addr-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
          .addr-table > tbody > tr > td { vertical-align: top; padding: 0; width: 50%; }
          .addr-table > tbody > tr > td:first-child { padding-right: 20px; }
          .addr-table > tbody > tr > td:last-child { padding-left: 20px; }
          .addr-label { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 600; margin-bottom: 4px; display: block; }
          .addr-name { font-weight: 700; font-size: 11px; line-height: 1.5; color: ${textColor}; }
          .addr-company { font-size: 10px; color: #334155; line-height: 1.5; font-weight: 500; }
          .addr-detail { font-size: 10px; color: #475569; line-height: 1.5; }
          .addr-email { color: ${accentColor}; font-size: 10px; line-height: 1.5; font-weight: 500; }
          
          .ref-table { width: 100%; border-collapse: collapse; background: ${headerBgColor}; border: 1px solid ${accentColor}40; border-radius: 4px; margin-bottom: 12px; }
          .ref-table td { padding: 8px 6px; text-align: center; border-left: 1px solid ${accentColor}30; vertical-align: middle; }
          .ref-table td:first-child { border-left: none; }
          .ref-label { font-size: 8px; color: #64748b; text-transform: uppercase; display: block; font-weight: 600; }
          .ref-value { font-size: 10px; font-weight: 600; margin-top: 2px; display: block; color: ${textColor}; }
          
          .section-title { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid ${primaryColor}; padding-bottom: 3px; margin-bottom: 0; color: ${primaryColor}; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
          .items-table th { text-align: left; padding: 3px 4px; font-size: 8px; color: #64748b; text-transform: uppercase; font-weight: 700; border-bottom: 1px solid ${accentColor}40; }
          .items-table td { padding: 4px 4px; border-bottom: 1px solid ${accentColor}20; font-size: 10px; color: ${textColor}; }
          
          .totals-wrapper { display: flex; justify-content: space-between; gap: 16px; margin-top: 8px; padding-top: 8px; border-top: 1px solid ${accentColor}40; }
          .payments-section { flex: 1; max-width: 55%; }
          .payments-header { font-size: 8px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; font-weight: 600; }
          .payment-row { font-size: 10px; padding: 2px 0; display: flex; justify-content: space-between; gap: 12px; color: ${textColor}; }
          .payment-row .method { color: #475569; }
          .payment-row .amount { color: #16a34a; font-weight: 600; white-space: nowrap; }
          .totals-section-right { width: 170px; }
          .total-row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 10px; color: #475569; }
          .total-row.final { font-weight: bold; font-size: 12px; color: ${textColor}; padding-top: 4px; }
          .total-row.final .amount { color: ${primaryColor}; }
          .balance-due-row { display: flex; justify-content: space-between; padding: 4px 0; border-top: 1px solid ${primaryColor}; margin-top: 4px; }
          .balance-due-row .label { font-weight: bold; font-size: 11px; color: ${textColor}; }
          .balance-due-row .amount { font-weight: bold; font-size: 13px; color: ${textColor}; }
          .balance-due-row .amount.overdue { color: #dc2626; }
          .balance-due-row .amount.paid { color: #16a34a; }
          
          .terms { margin-top: 8px; padding-top: 6px; border-top: 1px solid ${accentColor}40; }
          .terms-title { font-size: 8px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 2px; font-weight: 600; }
          .terms-text { font-size: 10px; color: #334155; white-space: pre-wrap; line-height: 1.4; }
          
          .footer { background: ${headerBgColor}; padding: 8px 14px; margin: 8px 28px 8px; border-radius: 6px; }
          .footer-contact { display: flex; justify-content: center; gap: 16px; font-size: 10px; color: #334155; margin-bottom: 3px; font-weight: 500; }
          .footer-text { font-size: 10px; color: #64748b; text-align: center; }
          .pay-qr { float: right; display: flex; flex-direction: column; align-items: center; gap: 3px; margin: 12px 0 8px 16px; padding: 8px 10px; background: ${headerBgColor}; border: 1px solid ${accentColor}40; border-radius: 6px; }
          .pay-qr-img { width: 60px; height: 60px; }
          .pay-qr-title { font-size: 8px; font-weight: 700; color: ${primaryColor}; text-transform: uppercase; letter-spacing: 0.6px; white-space: nowrap; }
          .bottom-area { margin-top: 8px; }
          
          @media print {
            @page { size: letter; margin: 0.25in; }
            body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .invoice { max-width: none; }
          }
        </style>
      </head>
      <body>
        <div class="invoice">
          <div class="header">
            ${brandingLogo 
              ? `<img src="${brandingLogo}" alt="Logo" class="logo">`
              : `<div class="company-name">${brandingName}</div>`
            }
            ${brandingTagline ? `<div class="tagline">${brandingTagline}</div>` : ''}
          </div>
          
          <div class="meta-wrapper">
            <table class="meta-table">
              <tr>
                <td>
                  <span class="meta-label">Invoice #</span>
                  <span class="meta-value">${invoice.invoice_number}</span>
                </td>
                <td>
                  <span class="meta-label">Issue Date</span>
                  <span class="meta-value">${formatDate(invoice.invoice_date)}</span>
                </td>
                <td>
                  <span class="meta-label">Due Date</span>
                  <span class="meta-value">${formatDate(invoice.due_date)}</span>
                </td>
                <td>
                  <span class="meta-label">Terms</span>
                  <span class="meta-value">${paymentTermsLabels[invoice.payment_terms] || invoice.payment_terms}</span>
                </td>
              </tr>
            </table>
          </div>
          
          <div class="content">
            <table class="addr-table">
              <tr>
                <td>
                  <span class="addr-label">From</span>
                  <div class="addr-name">${profile?.display_name || ''}</div>
                  ${profile?.company_name ? `<div class="addr-company">${profile.company_name}</div>` : ''}
                  <div class="addr-detail">${serverAddr}</div>
                  ${profile?.phone ? `<div class="addr-detail">${profile.phone}</div>` : ''}
                </td>
                <td>
                  <span class="addr-label">Bill To</span>
                  <div class="addr-name">${billToName}</div>
                  ${billToCompany ? `<div class="addr-company">${billToCompany}</div>` : ''}
                  <div class="addr-detail">${billToAddress}</div>
                  ${invoice.client?.email ? `<div class="addr-email">${invoice.client.email}</div>` : ''}
                </td>
              </tr>
            </table>
            
            ${refFields.length > 0 ? `
              <table class="ref-table">
                <tr>
                  ${refFields.map((f, i) => `
                    <td>
                      <span class="ref-label">${f.label}</span>
                      <span class="ref-value">${f.value}</span>
                    </td>
                  `).join('')}
                </tr>
              </table>
            ` : ''}
            
            <div class="section-title">Services & Charges</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style="text-align: right; width: 60px;">Qty</th>
                  <th style="text-align: right; width: 80px;">Rate</th>
                  <th style="text-align: right; width: 90px;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${lineItemsHTML}
              </tbody>
            </table>
            
            <!-- Totals Wrapper: Payments on left, Totals on right -->
            <div class="totals-wrapper">
              ${invoice.payments && invoice.payments.length > 0 ? `
                <!-- Payments Received Section (Left) -->
                <div class="payments-section">
                  <div class="payments-header">Payments Received</div>
                  ${invoice.payments.map(p => {
                    const paymentDetail = (p.notes && p.notes.trim()) ? p.notes : p.payment_method;
                    return `
                      <div class="payment-row">
                        <span class="method">${formatDate(p.payment_date)}${paymentDetail ? ` (${paymentDetail})` : ''}</span>
                        <span class="amount">-${formatCurrency(p.amount)}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              ` : '<div></div>'}
              
              <!-- Totals Section (Right) -->
              <div class="totals-section-right">
                ${invoice.tax_rate > 0 ? `
                  <div class="total-row">
                    <span>Subtotal</span>
                    <span>${formatCurrency(invoice.subtotal)}</span>
                  </div>
                  <div class="total-row">
                    <span>Tax (${invoice.tax_rate}%)</span>
                    <span>${formatCurrency(invoice.tax_amount)}</span>
                  </div>
                ` : ''}
                <div class="total-row final">
                  <span>Total</span>
                  <span class="amount">${formatCurrency(invoice.total)}</span>
                </div>
                ${invoice.payments && invoice.payments.length > 0 ? `
                  <div class="balance-due-row">
                    <span class="label">Balance Due</span>
                    <span class="amount ${(invoice.balance_due || 0) > 0 ? 'overdue' : 'paid'}">${formatCurrency(invoice.balance_due || 0)}</span>
                  </div>
                ` : ''}
              </div>
            </div>
            
            ${(qrHTML || invoice.notes || defaultTerms) ? `
              <div class="bottom-area">
                ${invoice.notes ? `
                  <div class="terms">
                    <div class="terms-title">Notes</div>
                    <div class="terms-text">${invoice.notes}</div>
                  </div>
                ` : ''}
                ${defaultTerms ? `
                  <div class="terms">
                    ${qrHTML}
                    <div class="terms-title">Terms & Conditions</div>
                    <div class="terms-text">${defaultTerms}</div>
                    <div style="clear: both;"></div>
                  </div>
                ` : (qrHTML ? `${qrHTML}<div style="clear: both;"></div>` : '')}
              </div>
            ` : ''}
          </div>
          
          ${(footerPhone || footerEmail || footerWebsite || footerText) ? `
            <div class="footer">
              ${(footerPhone || footerEmail || footerWebsite) ? `
                <div class="footer-contact">
                  ${footerPhone ? `<span>${footerPhone}</span>` : ''}
                  ${footerEmail ? `<span>${footerEmail}</span>` : ''}
                  ${footerWebsite ? `<span>${footerWebsite}</span>` : ''}
                </div>
              ` : ''}
              <div class="footer-text">${footerText || 'Thank you for your business'}</div>
            </div>
          ` : ''}
        </div>
      </body>
      </html>
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatePrintHTML());
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleDownload = async () => {
    try {
      // Dynamically import html2pdf
      const html2pdf = (await import('html2pdf.js')).default;
      
      // Create a temporary container with the HTML
      const container = document.createElement('div');
      container.innerHTML = generatePrintHTML();
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);
      
      const element = container.querySelector('.invoice');
      
      // Generate filename: "Invoice SRV-2026-00001"
      const invoiceNum = invoice.invoice_number || 'Draft';
      const filename = `Invoice ${invoiceNum}.pdf`;
      
      const opt = {
        margin: [0.35, 0.4, 0.35, 0.4],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          logging: false,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], before: [], after: [], avoid: ['tr', 'td', '.totals-box', '.terms', '.footer', '.pay-qr'] }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      // Clean up
      document.body.removeChild(container);
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to download. Using print dialog instead.');
      handlePrint();
    }
  };

  const serverAddress = profile?.server_address;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto p-4 md:p-6">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-white flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm md:text-base">
              Invoice {invoice.invoice_number}
              {/* Only show Paid timestamp when invoice is FULLY PAID (balance = 0) */}
              {invoice.paid_at && invoice.status === 'paid' && (invoice.total - (invoice.total_paid || 0)) === 0 && (
                <span className="text-green-400 ml-2">| Paid: {formatPaidDate(invoice.paid_at)}</span>
              )}
            </span>
            <Badge className={`${statusConfig[invoice.status]?.color || 'bg-slate-500'} text-white text-xs`}>
              {statusConfig[invoice.status]?.label || invoice.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Printable Invoice Content */}
        <div 
          ref={printRef} 
          className="bg-white rounded-lg overflow-hidden print:shadow-none"
          style={{ fontFamily: fontFamilyStyle, color: textColor }}
        >
          {/* Header - Clean centered logo with matching width */}
          <div className="px-5 md:px-8 pt-4 pb-2">
            <div 
              className="rounded-lg py-4 text-center"
              style={{ backgroundColor: headerBgColor }}
            >
              {brandingLogo ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={brandingLogo} 
                    alt="Logo" 
                    className={`${logoSizeClasses[logoSize] || logoSizeClasses.medium} object-contain`}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {brandingTagline && <p className="text-xs md:text-sm mt-2" style={{ color: '#475569' }}>{brandingTagline}</p>}
                </div>
              ) : (
                <div>
                  <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ color: primaryColor }}>{brandingName}</h1>
                  {brandingTagline && <p className="text-xs md:text-sm mt-1" style={{ color: '#475569' }}>{brandingTagline}</p>}
                </div>
              )}
            </div>
          </div>
          
          {/* Invoice Meta Bar - Core info with dividers */}
          <div className="px-5 md:px-8 py-2">
            <div 
              className="rounded-lg border py-2"
              style={{ backgroundColor: headerBgColor, borderColor: accentColor + '40' }}
            >
              <div className="flex justify-between items-center text-center">
                <div className="flex-1">
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Invoice #</p>
                  <p className="font-mono font-semibold text-[11px] md:text-xs">
                    {invoice.invoice_number || <span style={{ color: '#64748b', fontStyle: 'italic' }}>Draft</span>}
                  </p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Issue Date</p>
                  <p className="font-semibold text-[11px] md:text-xs">{formatDate(invoice.invoice_date)}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Due Date</p>
                  <p className="font-semibold text-[11px] md:text-xs">{formatDate(invoice.due_date)}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Terms</p>
                  <p className="font-semibold text-[11px] md:text-xs">{paymentTermsLabels[invoice.payment_terms] || invoice.payment_terms}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="px-5 md:px-8 py-4 md:py-5">
            {/* FROM and BILL TO - Always side by side */}
            <div className="grid grid-cols-2 gap-4 md:gap-8 mb-6">
              <div>
                <p className="text-[10px] md:text-xs uppercase tracking-wider font-medium mb-2" style={{ color: '#64748b', fontWeight: 600 }}>From</p>
                <div>
                  <p className="font-semibold text-sm md:text-base">{profile?.display_name || 'Process Server'}</p>
                  {profile?.company_name && <p className="text-xs md:text-sm" style={{ color: '#334155', fontWeight: 500 }}>{profile.company_name}</p>}
                  {serverAddress && (
                    <>
                      {serverAddress.street && <p className="text-xs md:text-sm" style={{ color: '#475569' }}>{serverAddress.street}</p>}
                      <p className="text-xs md:text-sm" style={{ color: '#475569' }}>
                        {[serverAddress.city, serverAddress.state, serverAddress.zip_code].filter(Boolean).join(', ')}
                      </p>
                    </>
                  )}
                  {profile?.phone && <p className="text-xs md:text-sm" style={{ color: '#475569' }}>{profile.phone}</p>}
                </div>
              </div>
              <div>
                <p className="text-[10px] md:text-xs uppercase tracking-wider font-medium mb-2" style={{ color: '#64748b', fontWeight: 600 }}>Bill To</p>
                <div>
                  {/* Show company name prominently if client is linked to a company */}
                  {invoice.client?.company ? (
                    <>
                      <p className="font-semibold text-sm md:text-base">{invoice.client.company.name}</p>
                      {invoice.client?.name && <p className="text-xs md:text-sm" style={{ color: '#334155', fontWeight: 500 }}>{invoice.client.name}{invoice.client?.title ? ` (${invoice.client.title})` : ''}</p>}
                      {(invoice.client?.address_street || invoice.client?.company?.address_street) && (
                        <p className="text-xs md:text-sm" style={{ color: '#475569' }}>
                          {invoice.client?.address_street || invoice.client?.company?.address_street}
                        </p>
                      )}
                      <p className="text-xs md:text-sm" style={{ color: '#475569' }}>
                        {[
                          invoice.client?.address_city || invoice.client?.company?.address_city, 
                          invoice.client?.address_state || invoice.client?.company?.address_state, 
                          invoice.client?.address_zip || invoice.client?.company?.address_zip
                        ].filter(Boolean).join(', ')}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-sm md:text-base">{invoice.client?.name}</p>
                      {invoice.client?.company_name && <p className="text-xs md:text-sm" style={{ color: '#334155', fontWeight: 500 }}>{invoice.client.company_name}</p>}
                      {invoice.client?.address_street && <p className="text-xs md:text-sm" style={{ color: '#475569' }}>{invoice.client.address_street}</p>}
                      <p className="text-xs md:text-sm" style={{ color: '#475569' }}>
                        {[invoice.client?.address_city, invoice.client?.address_state, invoice.client?.address_zip].filter(Boolean).join(', ')}
                      </p>
                    </>
                  )}
                  {invoice.client?.email && <p className="text-xs md:text-sm" style={{ color: accentColor }}>{invoice.client.email}</p>}
                </div>
              </div>
            </div>

            {/* Optional Reference Fields - Centered, full width */}
            {(invoice.entity_served || invoice.case_number || invoice.reference_number || invoice.service_date) && (
              <div 
                className="mb-4 py-2.5 rounded-lg border"
                style={{ backgroundColor: headerBgColor, borderColor: accentColor + '40' }}
              >
                <div className="flex justify-center items-center text-center" style={{ borderColor: accentColor + '30' }}>
                  {invoice.entity_served && (
                    <div className="flex-1 px-4 border-r" style={{ borderColor: accentColor + '30' }}>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Entity Served</p>
                      <p className="text-sm font-medium">{invoice.entity_served}</p>
                    </div>
                  )}
                  {invoice.case_number && (
                    <div className="flex-1 px-4 border-r" style={{ borderColor: accentColor + '30' }}>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Case #</p>
                      <p className="text-sm font-medium">{invoice.case_number}</p>
                    </div>
                  )}
                  {invoice.reference_number && (
                    <div className="flex-1 px-4 border-r" style={{ borderColor: accentColor + '30' }}>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Ref #</p>
                      <p className="text-sm font-medium">{invoice.reference_number}</p>
                    </div>
                  )}
                  {invoice.service_date && (
                    <div className="flex-1 px-4">
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#64748b', fontWeight: 600 }}>Service Date</p>
                      <p className="text-sm font-medium">{formatDate(invoice.service_date)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Line Items Header */}
            <div className="border-b-2 pb-2 mb-3" style={{ borderColor: primaryColor }}>
              <p className="text-xs md:text-sm font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>Services & Charges</p>
            </div>

            {/* Line Items - Desktop Table */}
            <div className="hidden md:block">
              <table className="w-full mb-4">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase">
                    <th className="text-left py-2 font-medium">Description</th>
                    <th className="text-right py-2 font-medium w-16">Qty</th>
                    <th className="text-right py-2 font-medium w-24">Rate</th>
                    <th className="text-right py-2 font-medium w-24">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoice.line_items?.map((item, idx) => {
                    const isProcessingFee = item.item_type === 'processing_fee';
                    return (
                      <tr key={idx}>
                        <td className="py-3">
                          <p className="font-medium text-slate-800">
                            {item.description || (isProcessingFee ? `Processing Fee (${item.unit_price}%)` : formatItemType(item.item_type))}
                          </p>
                        </td>
                        <td className="py-3 text-right text-slate-600">
                          {isProcessingFee ? '-' : item.quantity}
                        </td>
                        <td className="py-3 text-right text-slate-600">
                          {isProcessingFee ? `${item.unit_price}%` : formatCurrency(item.unit_price)}
                        </td>
                        <td className="py-3 text-right font-semibold text-slate-800">{formatCurrency(item.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          
            {/* Line Items - Mobile Cards */}
            <div className="md:hidden space-y-2 mb-4">
              {invoice.line_items?.map((item, idx) => {
                const isProcessingFee = item.item_type === 'processing_fee';
                return (
                  <div key={idx} className="bg-slate-50 rounded p-3">
                    <div className="flex justify-between items-start mb-1">
                      <p className="font-medium text-sm text-slate-800 flex-1 pr-2">
                        {item.description || (isProcessingFee ? `Processing Fee (${item.unit_price}%)` : formatItemType(item.item_type))}
                      </p>
                      <p className="font-semibold text-sm text-slate-800">{formatCurrency(item.total)}</p>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {isProcessingFee ? `${item.unit_price}%` : `${item.quantity} × ${formatCurrency(item.unit_price)}`}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Totals Section - Two column layout when payments exist */}
            <div className="border-t border-slate-200 pt-3">
              <div className={`flex ${invoice.payments && invoice.payments.length > 0 ? 'justify-between gap-6' : 'justify-end'}`}>
                {/* Payment History - Left side (only if payments exist) */}
                {invoice.payments && invoice.payments.length > 0 && (
                  <div className="flex-1 max-w-[55%]">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Payments Received</p>
                    {invoice.payments.map((payment, idx) => {
                      // Show notes if provided, otherwise show payment method
                      const paymentDetail = (payment.notes && payment.notes.trim()) 
                        ? payment.notes 
                        : payment.payment_method;
                      return (
                        <div key={payment.id || idx} className="py-1 text-xs">
                          <div className="flex justify-between items-start gap-3">
                            <span className="text-slate-600">
                              {formatDate(payment.payment_date)}
                              {paymentDetail && <span className="text-slate-500"> ({paymentDetail})</span>}
                            </span>
                            <span className="text-green-600 font-medium whitespace-nowrap">-{formatCurrency(payment.amount)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Totals - Right side */}
                <div className={`${invoice.payments && invoice.payments.length > 0 ? 'w-48' : 'w-full md:w-64'}`}>
                  {/* Only show subtotal if there's tax */}
                  {invoice.tax_rate > 0 && (
                    <>
                      <div className="flex justify-between py-1.5 text-xs md:text-sm">
                        <span className="text-slate-500">Subtotal</span>
                        <span className="text-slate-700">{formatCurrency(invoice.subtotal)}</span>
                      </div>
                      <div className="flex justify-between py-1.5 text-xs md:text-sm">
                        <span className="text-slate-500">Tax ({invoice.tax_rate}%)</span>
                        <span className="text-slate-700">{formatCurrency(invoice.tax_amount)}</span>
                      </div>
                    </>
                  )}
                  <div className={`flex justify-between py-2 ${invoice.tax_rate > 0 ? 'mt-1 border-t-2 border-slate-800' : ''}`}>
                    <span className="font-bold text-sm md:text-base text-slate-800">Total</span>
                    <span className="font-bold text-base md:text-lg text-slate-800">{formatCurrency(invoice.total)}</span>
                  </div>
                  
                  {/* Balance Due - show when there are payments */}
                  {invoice.payments && invoice.payments.length > 0 && (
                    <div className="flex justify-between py-2 mt-1 border-t-2 border-slate-800">
                      <span className="font-bold text-sm md:text-base text-slate-800">Balance Due</span>
                      <span className={`font-bold text-base md:text-lg ${(invoice.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(invoice.balance_due || 0)}
                      </span>
                    </div>
                  )}
                  
                  {/* Show Balance Due for unpaid invoices without payments */}
                  {(!invoice.payments || invoice.payments.length === 0) && invoice.status !== 'paid' && invoice.status !== 'void' && (
                    <div className="flex justify-between py-1 text-xs mt-1">
                      <span className="text-slate-500">Balance Due</span>
                      <span className="text-slate-700 font-semibold">{formatCurrency(invoice.total)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes & Terms — Scan-to-Pay QR floats INSIDE the Terms section (text wraps around it) */}
            {(qrDataUrl || invoice.notes || defaultTerms) && (
              <div className="mt-4">
                {invoice.notes && (
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Notes</p>
                    <p className="text-slate-600 whitespace-pre-wrap text-xs md:text-sm">{invoice.notes}</p>
                  </div>
                )}
                {defaultTerms ? (
                  <div className="pt-4 border-t border-slate-200 mt-4">
                    {qrDataUrl && (
                      <div className="float-right ml-4 mb-2 flex flex-col items-center gap-1 p-2 rounded-md border" style={{ borderColor: accentColor + '40' }} data-testid="invoice-pay-qr">
                        <img src={qrDataUrl} alt="Scan to pay" className="w-[60px] h-[60px]" />
                        <span className="text-[8px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: primaryColor }}>Scan to Pay</span>
                      </div>
                    )}
                    <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Terms & Conditions</p>
                    <p className="text-slate-600 whitespace-pre-wrap text-xs md:text-sm">{defaultTerms}</p>
                    <div className="clear-both" />
                  </div>
                ) : (qrDataUrl && (
                  <div className="flex justify-end mt-4">
                    <div className="flex flex-col items-center gap-1 p-2 rounded-md border" style={{ borderColor: accentColor + '40' }} data-testid="invoice-pay-qr">
                      <img src={qrDataUrl} alt="Scan to pay" className="w-[60px] h-[60px]" />
                      <span className="text-[8px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: primaryColor }}>Scan to Pay</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-slate-50 px-5 md:px-8 py-3 border-t border-slate-200 print:bg-white">
            {/* Contact Info Row - Full width, spaced out */}
            {(footerPhone || footerEmail || footerWebsite) && (
              <div className="flex justify-center items-center gap-6 text-[10px] md:text-xs text-slate-500 mb-2">
                {footerPhone && <span>{footerPhone}</span>}
                {footerEmail && <span>{footerEmail}</span>}
                {footerWebsite && <span>{footerWebsite}</span>}
              </div>
            )}
            {/* Additional Footer Text - Separate row, always centered */}
            <p className="text-[10px] md:text-xs text-slate-400 text-center">
              {footerText || 'Thank you for your business'}
            </p>
          </div>
        </div>

        {/* Actions - Smaller buttons, centered, NO ICONS */}
        <div className="flex flex-wrap justify-center gap-1.5 mt-4">
          {/* Edit button - available for all except void */}
          {invoice.status !== 'void' && (
            <Button onClick={() => onAction?.('edit', invoice)} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3" data-testid="edit-invoice-btn">
              Edit
            </Button>
          )}
          {invoice.status === 'draft' && (
            <>
              <Button onClick={handleSend} className="bg-blue-600 hover:bg-blue-700 text-xs h-8 px-3" data-testid="send-invoice-btn">
                Send
              </Button>
              <Button onClick={handleIssue} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3" data-testid="issue-invoice-btn">
                Issue
              </Button>
            </>
          )}
          {(invoice.status === 'sent' || invoice.status === 'viewed') && (
            <Button onClick={handleSend} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3" data-testid="resend-invoice-btn">
              Resend
            </Button>
          )}
          {invoice.status === 'overdue' && (
            <Button onClick={handleSendReminder} className="bg-red-600 hover:bg-red-700 text-xs h-8 px-3" data-testid="send-reminder-btn">
              Send Reminder
            </Button>
          )}
          {['sent', 'viewed', 'overdue'].includes(invoice.status) && (
            <Button onClick={handleMarkPaid} className="bg-green-600 hover:bg-green-700 text-xs h-8 px-3" data-testid="mark-paid-btn">
              Mark Paid
            </Button>
          )}
          {invoice.status === 'paid' && (
            <Button onClick={() => onAction?.('addCharges', invoice)} className="bg-amber-600 hover:bg-amber-700 text-xs h-8 px-3" data-testid="add-charges-btn">
              Add Charges
            </Button>
          )}
          {invoice.payments && invoice.payments.length > 0 && invoice.status !== 'void' && (
            <Button onClick={() => openEditPayment()} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3" data-testid="edit-payment-btn">
              Edit Payment
            </Button>
          )}
          <Button onClick={handlePrint} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3" data-testid="print-invoice-btn">
            Print
          </Button>
          <Button onClick={handleDownload} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3" data-testid="download-invoice-btn">
            Download
          </Button>
          {invoice.status !== 'draft' && (
            <Button onClick={handleCopyLink} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3">
              Copy Link
            </Button>
          )}
          {['sent', 'viewed', 'overdue', 'paid'].includes(invoice.status) && (
            <Button onClick={handleVoid} variant="outline" className="border-red-600 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-300 text-xs h-8 px-3">
              Void
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} variant="ghost" className="text-slate-400 hover:text-white hover:bg-slate-800 text-xs h-8 px-3" data-testid="close-invoice-btn">
            Done
          </Button>
        </div>

        {/* Status Info - Sent left, Viewed right (Paid is now in header) */}
        {invoice.sent_at && (
          <div className="mt-3 text-xs text-slate-400 flex justify-between items-center">
            <span>Sent: {formatDateTime(invoice.sent_at)}</span>
            {invoice.viewed_at && <span>Viewed: {formatDateTime(invoice.viewed_at)}</span>}
          </div>
        )}
        
        {/* Void Info */}
        {invoice.status === 'void' && invoice.void_info && (
          <div className="mt-3 bg-red-900/20 border border-red-800/50 rounded-lg p-3">
            <p className="text-red-400 text-sm font-medium">Invoice Voided</p>
            <p className="text-red-300/80 text-xs mt-1">Reason: {invoice.void_info}</p>
            {invoice.voided_at && (
              <p className="text-red-300/60 text-xs mt-1">Voided: {formatDateTime(invoice.voided_at)}</p>
            )}
          </div>
        )}
      </DialogContent>

      {/* Edit Payment Dialog */}
      <Dialog open={showEditPaymentDialog} onOpenChange={setShowEditPaymentDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {invoice.payments && invoice.payments.length > 1 && (
              <div className="space-y-2">
                <Label className="text-slate-300">Which Payment</Label>
                <Select value={editingPaymentId} onValueChange={(id) => openEditPayment(invoice.payments.find(p => p.id === id))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {invoice.payments.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-white">
                        {formatDate(p.payment_date)} — {formatCurrency(p.amount)} ({p.payment_method || 'Manual'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">Payment Method</Label>
              <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="edit-payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {PAYMENT_METHODS.map(method => (
                    <SelectItem key={method.value} value={method.value} className="text-white">
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Payment Date</Label>
              <Input
                type="date"
                value={editPaymentDate}
                onChange={(e) => setEditPaymentDate(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="edit-payment-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Notes (optional)</Label>
              <Input
                value={editPaymentNotes}
                onChange={(e) => setEditPaymentNotes(e.target.value)}
                placeholder="e.g., Check #1234, Transaction ID"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowEditPaymentDialog(false)}
                className="flex-1 border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmEditPayment}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-payment-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Payment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <p className="text-slate-400 text-sm">Amount Due</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(invoice.total - (invoice.total_paid || 0))}</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {PAYMENT_METHODS.map(method => (
                    <SelectItem key={method.value} value={method.value} className="text-white">
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="payment-date-input"
              />
              <p className="text-xs text-slate-500">When was the payment received? Defaults to today.</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Notes (optional)</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="e.g., Check #1234, Transaction ID"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowPaymentDialog(false)}
                className="flex-1 border-slate-700 text-slate-300"
                data-testid="cancel-payment-btn"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmMarkPaid}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="record-payment-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Void Invoice Dialog */}
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Void Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3">
              <p className="text-red-400 text-sm font-medium">This action cannot be undone</p>
              <p className="text-red-300/70 text-xs mt-1">
                Voiding an invoice will permanently mark it as cancelled. The invoice will remain in your records but cannot be restored.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Reason for Voiding *</Label>
              <Select value={voidReason} onValueChange={setVoidReason}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {VOID_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value} className="text-white">
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Additional Notes (optional)</Label>
              <Input
                value={voidNotes}
                onChange={(e) => setVoidNotes(e.target.value)}
                placeholder="e.g., Additional details about the void"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowVoidDialog(false)}
                className="flex-1 border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmVoid}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <Ban className="h-4 w-4 mr-2" />
                Void Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Send Payment Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3">
              <p className="text-red-400 text-sm font-medium">Overdue Invoice</p>
              <p className="text-red-300/70 text-xs mt-1">
                This will send a payment reminder email to {invoice?.client?.name || 'the client'} at {invoice?.client?.email || 'their email address'}.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <p className="text-slate-400 text-sm">Balance Due</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(invoice?.total - (invoice?.total_paid || 0))}</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Custom Message (optional)</Label>
              <Input
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                placeholder="Add a personal note to the reminder..."
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500">This message will appear in the reminder email.</p>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowReminderDialog(false)}
                className="flex-1 border-slate-700 text-slate-300"
                disabled={isSendingReminder}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmSendReminder}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={isSendingReminder}
              >
                {isSendingReminder ? 'Sending...' : 'Send Reminder'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
